export type DemoVehicleAvailability = {
  vehicleAssignmentId: string;
  vehicleAvailabilityId: string;
  status: string;
  startDatetime: string;
  endDatetime: string;
  reason: string | null;
  comments: string | null;
  isUnavailableAllDay: boolean;
  isActive: boolean;
};

export type TestDriveVehicle = {
  agencyId: string;
  make: string;
  model: string;
  trim?: string;
  /** Full display label, e.g. `Lucid Air Touring`. */
  description: string;
  durationMinutes: number;
  availability: boolean;
  vehicleAssignmentId?: string;
  locationId?: string;
  vehicleId?: string;
  vin?: string;
  demoVehicleAvailability?: DemoVehicleAvailability[];
};

export type TestDriveVehiclesResponse = TestDriveVehicle[];

export type UseGetTestDriveVehiclesParams = {
  agencyId: string;
  /** Prospect's business partner location; omit to let the backend apply its default. */
  locationId?: string;
  availableOnly?: boolean;
  enabled?: boolean;
};

export type TestDriveSlotEventConfig = {
  eventConfigId: string;
  vin: string;
  make: string;
  model: string;
  trim: string;
  description: string;
};

export const TEST_DRIVE_SLOT_AVAILABLE = 'AVAILABLE';

export type TestDriveSlotDetail = {
  /** UTC instant, e.g. `2026-09-19T17:00:00Z`. */
  startTime: string;
  endTime: string;
  timezone: string;
  slotAvailable: boolean;
  slotAvailabilityStatus: string;
  tdEventConfig?: TestDriveSlotEventConfig[];
};

export type TestDriveAvailableSlotsResponse = {
  agencyId: string;
  model: string;
  trim: string;
  selectedDate: string;
  slotDetails: TestDriveSlotDetail[];
  /** Extended test drives only: every pickup and return time pairing that can be booked. */
  availableWindowCombinations?: TestDriveWindowCombination[];
  /** Length of the pickup and return handover, in minutes. */
  handoverDurationMinutes?: number;
  locationTimezone?: string;
  /** False when nothing can be booked; `availabilityStatus` then says why, e.g. `NO_DROP_OPERATING_SLOTS`. */
  isAvailable?: boolean;
  availabilityStatus?: string;
};

export type TestDriveWindowVehicle = {
  vin: string;
  vehicle_trim: string;
  vehicle_identifier: string;
  vehicle_round_robin_rank: number;
  test_drive_event_configuration_identifier: string;
};

export type TestDriveWindowAgent = {
  round_robin_rank: number;
  sales_consultant_identifier: string;
};

/** One bookable extended window; `*_local` is studio time without an offset, `*_utc` the instant. */
export type TestDriveWindowCombination = {
  pickup_datetime_utc: string;
  pickup_datetime_local: string;
  drop_datetime_utc: string;
  drop_datetime_local: string;
  available_vehicle_count?: number;
  /** The vehicle the API picked for this window. */
  recommended_vehicle?: TestDriveWindowVehicle;
  recommended_pickup_sales_agent?: TestDriveWindowAgent;
  recommended_drop_sales_agent?: TestDriveWindowAgent;
};

export type UseGetTestDriveAvailableSlotsParams = {
  agencyId: string;
  /** Prospect's business partner location; omit to let the backend apply its default. */
  locationId?: string;
  model: string;
  trim?: string;
  /** Studio-local calendar day, `YYYY-MM-DD`. */
  selectedDate: string;
  /** `STANDARD` or `EXTENDED`; omitted by callers that only book standard drives. */
  testDriveType?: string;
  /** Studio-local return day, `YYYY-MM-DD`; only sent for an extended test drive. */
  returnDate?: string;
  /** Booking being modified; its own slot is kept selectable instead of showing as taken. */
  excludeBookingId?: string;
  enabled?: boolean;
};

/**
 * Booking lifecycle status values. Mirrors `TestDriveBookingRecord.testDriveStatus` on the
 * x-sales `BookingController`, which is also what `BookingSearchRequest.status` filters by.
 */
export type BookingTestDriveStatus = 'Requested' | 'Scheduled' | 'Completed' | 'Cancelled';

/** Test drive type as stored by TDM (`STANDARD` / `EXTENDED`). */
export type BookingTestDriveType = 'STANDARD' | 'EXTENDED';

/**
 * Minimal prospect details enriched onto a booking record for display only (not for editing).
 * Mirrors a subset of x-sales's `ProspectDto`, resolved server-side from `prospectIdentifier`.
 */
export type BookingProspectDetails = {
  prospectIdentifier?: string;
  prospectFirstName?: string;
  prospectLastName?: string;
  prospectEmail?: string;
  prospectPhone?: string;
};

/**
 * Minimal Lucid Partner Portal user details enriched onto a booking record for display only.
 * Mirrors a subset of x-sales's `UserInfo`, resolved server-side from `businessPartnerUserId`.
 */
export type BookingUserDetails = {
  userId?: string;
  name?: string;
  role?: string;
  email?: string;
  phone?: string;
};

/**
 * A single persisted test-drive booking record, as returned by the booking search endpoint.
 * Field names mirror TDM's `TestDriveBookingResponse` exactly. `prospect`/`bookedByUser` are
 * enriched server-side (x-sales) for display and may be absent if the lookup failed or the
 * identifying field was null.
 */
export type TestDriveBookingRecord = {
  testDriveScheduleId: string;
  sfdcSalesEventId?: string;
  prospectIdentifier?: string;
  businessPartnerUserId?: string;
  businessPartnerOrganizationId?: string;
  /** Prospect details for display, enriched from prospect-hub. */
  prospect?: BookingProspectDetails;
  /** Lucid Partner Portal user who booked the test drive, for display. */
  bookedByUser?: BookingUserDetails;
  testDriveStatus?: BookingTestDriveStatus | string;
  /** Status as shown to users: `NO_SHOW` once marked, otherwise the upper-cased status. */
  effectiveStatus?: string;
  testDriveType?: BookingTestDriveType | string;
  /** Denormalized prospect full name (falls back to the enriched prospect server-side). */
  prospectFullName?: string;
  /** Host (Sales Consultant) display name. */
  hostName?: string;
  /** Scheduled start date/time in ISO-8601 format. */
  scheduledStartDatetime?: string;
  scheduledEndDatetime?: string;
  appointmentLocationName?: string;
  model?: string;
  trim?: string;
  vehicleDetails?: string;
  description?: string;
  vin?: string;
  bookingSource?: string;
  vehicleIdentifier?: string;
  vehicleOwnershipIdentifier?: string;
  hostUserIdentifier?: string;
  hostAssignmentStatus?: string;
  checkInDatetime?: string;
  checkOutDatetime?: string;
  /** Customer time zone, e.g. `America/Los_Angeles`. */
  timezone?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  createdDatetime?: string;
  updatedDatetime?: string;
};

/**
 * Search request for the cross-prospect agency test-drive management view. All fields are
 * optional; omit `pageNumber`/`pageSize` for the full unpaginated result set.
 */
export type BookingSearchRequest = {
  prospectId?: string;
  agencyId?: string;
  status?: string;
  salesConsultantId?: string;
  vehicleModel?: string;
  vin?: string;
  /** Appointment range start (inclusive), ISO-8601 instant. */
  appointmentStartDate?: string;
  /** Appointment range end (inclusive), ISO-8601 instant. */
  appointmentEndDate?: string;
  /** Global free-text search across ID, model, VIN, type, status and location. */
  searchText?: string;
  testDriveId?: string;
  testDriveType?: string;
  locationName?: string;
  /** Appointment end range start (inclusive), ISO-8601 instant. */
  appointmentEndFrom?: string;
  /** Appointment end range end (inclusive), ISO-8601 instant. */
  appointmentEndTo?: string;
  pageNumber?: number;
  pageSize?: number;
  /**
   * Supported keys: testDriveId, prospectFullName, model, vin, testDriveType,
   * appointmentStart, appointmentEnd, status, hostName, location, createdDatetime.
   */
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
};

/** Wraps the list of booking records returned by the booking search endpoint, with pagination metadata. */
export type TestDriveBookingsResponse = {
  bookings: TestDriveBookingRecord[];
  pageNumber?: number;
  pageSize?: number;
  totalCount?: number;
  totalPages?: number;
  currentPageCount?: number;
};

export type UseSearchBookingsParams = {
  prospectId?: string;
  status?: string;
  vehicleModel?: string;
  vin?: string;
  appointmentStartDate?: string;
  appointmentEndDate?: string;
  searchText?: string;
  testDriveId?: string;
  testDriveType?: string;
  locationName?: string;
  appointmentEndFrom?: string;
  appointmentEndTo?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  enabled?: boolean;
};

export type UseGetSalesConsultantsCalendarParams = {
  agencyId: string;
  /** Prospect's business partner location; omit to let the backend apply its default. */
  locationId?: string;
  /** Single day, `YYYY-MM-DD`; sent as both startDate and endDate. */
  date: string;
  enabled?: boolean;
};

export type SalesConsultantCalendarBooking = {
  bookingRequestId: string;
  prospectId?: string;
  assignedUserId?: string | null;
  bookingStatus: string;
  startDatetime: string;
  endDatetime: string;
};

export type SalesConsultantCalendarException = {
  exceptionStartDatetime: string;
  exceptionEndDatetime: string;
};

export type SalesConsultantCalendarAttendee = {
  user: {
    userId: string | null;
    name: string | null;
    email?: string | null;
    availabilityStatus?: 'AVAILABLE' | 'UNAVAILABLE' | 'UNAVAILABLE_FULL_DAY';
    isTestDriveHost: boolean;
  };
  bookings: SalesConsultantCalendarBooking[];
  exceptions?: SalesConsultantCalendarException[];
};

export type SalesConsultantsCalendarResponse = {
  agencyId: string;
  startDate: string;
  endDate: string;
  boardTimeZone: string;
  scheduleDays: { scheduleDate: string; attendees: SalesConsultantCalendarAttendee[] }[];
};
